import { supabase } from '../lib/supabase';

export const practiceAreasService = {
  // Get all practice areas with their related data
  async getAllPracticeAreas() {
    try {
      const { data, error } = await supabase?.from('practice_areas')?.select('*')?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform the data to match the frontend structure
      const transformedData = data?.map(practiceArea => ({
        ...practiceArea,
        image: practiceArea?.image_url, // Map image_url to image for frontend compatibility
        fullDescription: practiceArea?.full_description, // Map full_description to fullDescription
        services: practiceArea?.services || [], // Use JSON services directly
        specialties: practiceArea?.specialties || [] // Use JSON specialties directly
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching practice areas:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get practice area by slug
  async getPracticeAreaBySlug(slug) {
    try {
      const { data, error } = await supabase?.from('practice_areas')?.select('*')?.eq('slug', slug)?.single();

      if (error) {
        throw error;
      }

      // Transform the data to match the frontend structure
      const transformedData = {
        ...data,
        image: data?.image_url, // Map image_url to image for frontend compatibility
        fullDescription: data?.full_description, // Map full_description to fullDescription
        services: data?.services || [], // Use JSON services directly
        specialties: data?.specialties || [] // Use JSON specialties directly
      };

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching practice area:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get featured practice areas
  async getFeaturedPracticeAreas() {
    try {
      const { data, error } = await supabase?.from('practice_areas')?.select('*')?.eq('is_featured', true)?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform the data to match the frontend structure
      const transformedData = data?.map(practiceArea => ({
        ...practiceArea,
        image: practiceArea?.image_url, // Map image_url to image for frontend compatibility
        fullDescription: practiceArea?.full_description, // Map full_description to fullDescription
      }));

      return { data: transformedData, error: null };
    } catch (error) {
      console.log('Error fetching featured practice areas:', error);
      return { data: null, error: error?.message };
    }
  },

  // Get practice areas by category
  async getPracticeAreasByCategory(category) {
    try {
      const { data, error } = await supabase?.from('practice_areas')?.select('*')?.eq('category', category)?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.log('Error fetching practice areas by category:', error);
      return { data: null, error: error?.message };
    }
  },

  // Search practice areas
  async searchPracticeAreas(searchTerm) {
    try {
      const { data, error } = await supabase?.from('practice_areas')?.select('*')?.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)?.order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.log('Error searching practice areas:', error);
      return { data: null, error: error?.message };
    }
  },

  // Create a new practice area
  async createPracticeArea(practiceAreaData) {
    try {
      // Transform frontend field names to database field names
      const dbData = {
        ...practiceAreaData,
        full_description: practiceAreaData.fullDescription || practiceAreaData.full_description,
        image_url: practiceAreaData.image || practiceAreaData.image_url,
      };
      
      // Remove frontend-specific fields that don't exist in database
      delete dbData.fullDescription;
      delete dbData.image;
      
      const { data, error } = await supabase
        .from('practice_areas')
        .insert([dbData])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating practice area:', error);
      return { data: null, error: error?.message };
    }
  },

  // Update a practice area
  async updatePracticeArea(id, updates) {
    try {
      console.log('Service: Updating practice area with ID:', id);
      console.log('Service: Updates data:', updates);
      
      // Transform frontend field names to database field names
      const dbUpdates = {
        ...updates,
        full_description: updates.fullDescription || updates.full_description,
        image_url: updates.image || updates.image_url,
      };
      
      // Remove frontend-specific fields that don't exist in database
      delete dbUpdates.fullDescription;
      delete dbUpdates.image;
      
      console.log('Service: Transformed updates for database:', dbUpdates);
      
      const { data, error } = await supabase
        .from('practice_areas')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      console.log('Service: Supabase response - data:', data);
      console.log('Service: Supabase response - error:', error);

      if (error) {
        console.error('Service: Supabase error details:', error);
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Service: Error updating practice area:', error);
      console.error('Service: Error type:', typeof error);
      console.error('Service: Error message:', error?.message);
      console.error('Service: Full error object:', error);
      return { data: null, error: error?.message || error || 'Unknown error' };
    }
  },

  // Delete a practice area
  async deletePracticeArea(id) {
    try {
      const { error } = await supabase
        .from('practice_areas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting practice area:', error);
      return { error: error?.message };
    }
  }
};